import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PencilIcon, BoltIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import seaWolfImage from '../assets/images/Image 3_Sea Wolf .png';
import seaWolfPodcast from '../assets/SeaWolf/Podcast/McKinsey Sea Wolf Game Walkthrough and Practice.wav';
import SeaWolfSolver from '../components/solver/SeaWolfSolver';
import SeaWolfResults from '../components/solver/SeaWolfResults';
import MicrobeCardsGrid from '../components/solver/MicrobeCards';
import ForTheGramMicrobeCardsGrid from '../components/solver/ForTheGramMicrobeCardsGrid';
import BioticMadnessMicrobeCardsGrid from '../components/solver/BioticMadnessMicrobeCardsGrid';
import FeelTheBurnMicrobeCardsGrid from '../components/solver/FeelTheBurnMicrobeCardsGrid';
import FlexForceMicrobeCardsGrid from '../components/solver/FlexForceMicrobeCardsGrid';
import SiteInfoCard from '../components/solver/SiteInfoCard';
import ForTheGramSiteInfoCard from '../components/solver/ForTheGramSiteInfoCard';
import BioticMadnessSiteInfoCard from '../components/solver/BioticMadnessSiteInfoCard';
import FeelTheBurnSiteInfoCard from '../components/solver/FeelTheBurnSiteInfoCard';
import FlexForceSiteInfoCard from '../components/solver/FlexForceSiteInfoCard';
import '../styles/FloatingSidebar.css';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import SEO from '../components/SEO';

const styles = `
  .feature-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    transform-style: preserve-3d;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.5s forwards;
    border: 1px solid rgba(59, 130, 246, 0.1);
  }

  .feature-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
    border-color: rgba(59, 130, 246, 0.2);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.9) 100%);
  }

  .feature-icon {
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
    border-radius: 12px;
    margin-bottom: 1rem;
  }

  .feature-card:hover .feature-icon {
    transform: scale(1.1) rotate(-5deg);
    background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%);
  }

  .section-title {
    background: linear-gradient(135deg, #000 0%, #333 25%, #000 50%, #666 75%, #000 100%);
    -webkit-background-clip: text;
    color: transparent;
    position: relative;
    display: inline-block;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.1);
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.3) 25%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.3) 75%, transparent 100%);
    border-radius: 2px;
  }

  .microbe-card {
    transition: all 0.2s ease-in-out;
  }

  .microbe-card:hover {
    transform: translateY(-2px);
  }

  .tip-box {
    background: linear-gradient(165deg, #F0F9FF 0%, #E0F2FE 100%);
    border-left: 4px solid #60A5FA;
    border-radius: 8px;
  }

  .scenario-card {
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%);
    border: 1px solid rgba(59, 130, 246, 0.1);
    transition: all 0.3s ease;
  }

  .scenario-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.25);
    border-color: rgba(59, 130, 246, 0.2);
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-content {
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.6s forwards;
  }

  .hero-image {
    opacity: 0;
    transform: translateX(20px);
    animation: fadeInRight 0.8s 0.2s forwards;
  }

  @keyframes fadeInRight {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes pulse-glow {
    0% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                  0 0 10px rgba(59, 130, 246, 0.3),
                  0 0 15px rgba(59, 130, 246, 0.2);
    }
    50% {
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8),
                  0 0 30px rgba(59, 130, 246, 0.5),
                  0 0 40px rgba(59, 130, 246, 0.3);
    }
    100% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                  0 0 10px rgba(59, 130, 246, 0.3),
                  0 0 15px rgba(59, 130, 246, 0.2);
    }
  }

  @keyframes text-glow {
    0% {
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.5),
                   0 0 10px rgba(255, 255, 255, 0.3);
    }
    50% {
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.8),
                   0 0 20px rgba(255, 255, 255, 0.5),
                   0 0 30px rgba(255, 255, 255, 0.3);
    }
    100% {
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.5),
                   0 0 10px rgba(255, 255, 255, 0.3);
    }
  }

  .coming-soon-banner {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .coming-soon-text {
    animation: text-glow 2s ease-in-out infinite;
  }

  .dataset-header {
    margin: 0.5rem 2rem 0.75rem 0.5rem;
    padding: 0.5rem 0.75rem;
    background: linear-gradient(165deg, #EFF6FF 0%, #DBEAFE 100%);
    border: 1px solid rgba(59, 130, 246, 0.2);
    border-radius: 0.5rem;
    position: relative;
    animation: pulse-glow 3s ease-in-out forwards;
    max-width: calc(100% - 3rem);
  }

  .dataset-header::before {
    content: '';
    position: absolute;
    inset: -1px;
    background: linear-gradient(165deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.2) 100%);
    border-radius: 0.5rem;
    z-index: -1;
    animation: pulse-border 3s ease-in-out forwards;
  }

  @keyframes pulse-glow {
    0% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                  0 0 10px rgba(59, 130, 246, 0.3),
                  0 0 15px rgba(59, 130, 246, 0.2);
    }
    50% {
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8),
                  0 0 30px rgba(59, 130, 246, 0.5),
                  0 0 40px rgba(59, 130, 246, 0.3);
    }
    100% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                  0 0 10px rgba(59, 130, 246, 0.3),
                  0 0 15px rgba(59, 130, 246, 0.2);
    }
  }

  @keyframes pulse-border {
    0% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.5;
    }
  }

  .dataset-header-text {
    font-size: 0.8125rem;
    line-height: 1.3;
    color: #1E40AF;
    margin: 0;
    animation: text-glow 3s ease-in-out forwards;
  }

  .floating-sidebar {
    position: fixed;
    right: 1rem;
    top: 6rem;
    width: 320px;
    max-height: calc(100vh - 8rem);
    overflow-y: auto;
    background: white;
    border-radius: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    padding: 1rem;
    z-index: 50;
  }

  .floating-sidebar::-webkit-scrollbar {
    width: 6px;
  }

  .floating-sidebar::-webkit-scrollbar-track {
    background: transparent;
  }

  .floating-sidebar::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    border-radius: 3px;
  }

  @media (max-width: 1280px) {
    .floating-sidebar {
      display: none;
    }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const pricingState = { scrollToPricing: true };

const solverHashTargetsSection = (hash = '') => hash.replace(/^#/, '').startsWith('solver');

export default function SeaWolfPage() {
  const [showScoringDetails, setShowScoringDetails] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeVideoTab, setActiveVideoTab] = useState('walkthrough2026');
  const [showEcosystemData, setShowEcosystemData] = useState(false);
  const [showMissionImpossible, setShowMissionImpossible] = useState(false);
  const [showFloatingMicrobes, setShowFloatingMicrobes] = useState(false);
  const [showForTheGram, setShowForTheGram] = useState(false);
  const [showFloatingForTheGram, setShowFloatingForTheGram] = useState(false);
  const [showBioticMadness, setShowBioticMadness] = useState(false);
  const [showFloatingBioticMadness, setShowFloatingBioticMadness] = useState(false);
  const [isFeelTheBurnExpanded, setIsFeelTheBurnExpanded] = useState(false);
  const [showFeelTheBurnSidebars, setShowFeelTheBurnSidebars] = useState(false);
  const [showFlexForce, setShowFlexForce] = useState(false);
  const [showFloatingFlexForce, setShowFloatingFlexForce] = useState(false);

  // Handle hash navigation
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (solverHashTargetsSection(hash)) {
        const solverElement = document.getElementById('solver');
        if (solverElement) {
          setTimeout(() => {
            solverElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 200);
        }
      }
    };

    // Handle initial load
    handleHashNavigation();

    // Handle hash changes
    const handleHashChange = () => {
      handleHashNavigation();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const scenarios = [
    {
      type: 'Ocean Harmony',
      score: '100%',
      description: 'Easy, perfect match exists',
      icon: '🌊'
    },
    {
      type: 'Deep Dive',
      score: '100%',
      description: 'Perfect match exists but hard to find',
      icon: '🤿'
    },
    {
      type: 'Turbulent Waters',
      score: '80%',
      description: 'Only 2 of 3 attributes are matchable',
      icon: '🌪️'
    },
    {
      type: 'Abyssal Challenge',
      score: '60%',
      description: 'Only 1 attribute match is possible',
      icon: '⚓'
    }
  ];

  const gameSteps = [
    {
      step: 1,
      title: 'Set Screening Filters',
      description: 'Choose 1 trait to prioritize and 1 attribute with range',
      icon: '🔍',
      tip: 'This step doesn\'t affect microbes shown. Just use it to internalize the site\'s targets.'
    },
    {
      step: 2,
      title: 'Categorize 10 Microbes',
      description: 'Assign microbes to Current Site, Next Site, or Discard',
      icon: '📋',
      tip: 'If it matches 2+ of 4 site needs (3 attributes + 1 trait), keep it.'
    },
    {
      step: 3,
      title: 'Add 4 More Microbes',
      description: 'Pick one from each set of 3 microbes to add to your pool',
      icon: '➕',
      tip: 'If your initial 6 microbes have no desired trait, prioritize adding some now.'
    },
    {
      step: 4,
      title: 'Choose 3 Microbes to Submit',
      description: 'Select the best 3 out of 10 based on scoring criteria',
      icon: '🎯',
      tip: 'Compare the sum of attribute values to the tripled value of the range.'
    },
    {
      step: 5,
      title: 'Review Saved Microbes',
      description: 'Decide whether to keep or return microbes for the next site',
      icon: '🔄',
      tip: 'You\'ll then screen 10 new ones (Step 2 restarts).'
    }
  ];

  const videoTabs = [
    {
      id: 'walkthrough2026',
      title: 'Sea Wolf 2026 Walkthrough',
      videoId: 'fBnGriXD7TU',
      startTime: 0,
    },
    {
      id: 'solver2025',
      title: 'Sea Wolf Solver Tutorial',
      videoId: 'rZjsPN6ZEaQ',
      startTime: 0,
    },
  ];

  return (
    <>
      <SEO
        title="Sea Wolf Guide & Solver | Cognidoor"
        description="Sea Wolf Guide & Solver: master the McKinsey Sea Wolf game with our expert guide and solver. Learn optimal strategies for microbe selection and ecosystem restoration."
        url="/seawolf"
        keywords={['mckinsey seawolf', 'seawolf game', 'mckinsey solve', 'strategy game', 'case prep']}
        noIndex
      />
      <div className="bg-gradient-to-b from-white via-blue-50/20 to-white text-gray-900 min-h-screen font-sans">
        {/* Side Navigation */}
        <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999] hidden md:flex">
          <div className="group relative">
            <a 
              href="#introduction" 
              className="flex items-center scroll-smooth"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('introduction')?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Introduction Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Introduction
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <PencilIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#game-flow" 
              className="flex items-center scroll-smooth"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('game-flow')?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Game Flow Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Game Flow
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <BoltIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#scenarios" 
              className="flex items-center scroll-smooth"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('scenarios')?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Scenarios Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Scenarios
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#strategy" 
              className="flex items-center scroll-smooth"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('strategy')?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Strategy Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Strategy
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <ChartBarIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#solver" 
              className="flex items-center scroll-smooth"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('solver')?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Solver Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Solver
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
            </a>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex md:hidden gap-4 z-[9999] bg-white/90 backdrop-blur-sm px-4 py-3 rounded-full shadow-lg">
          <a 
            href="#introduction" 
            className="p-2"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('introduction')?.scrollIntoView({ behavior: 'smooth' });
            }}
            aria-label="Introduction Section"
          >
            <PencilIcon className="w-5 h-5 text-gray-600" />
          </a>
          <a 
            href="#game-flow" 
            className="p-2"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('game-flow')?.scrollIntoView({ behavior: 'smooth' });
            }}
            aria-label="Game Flow Section"
          >
            <BoltIcon className="w-5 h-5 text-gray-600" />
          </a>
          <a 
            href="#scenarios" 
            className="p-2"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('scenarios')?.scrollIntoView({ behavior: 'smooth' });
            }}
            aria-label="Scenarios Section"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </a>
          <a 
            href="#strategy" 
            className="p-2"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('strategy')?.scrollIntoView({ behavior: 'smooth' });
            }}
            aria-label="Strategy Section"
          >
            <ChartBarIcon className="w-5 h-5 text-gray-600" />
          </a>
          <a 
            href="#solver" 
            className="p-2"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('solver')?.scrollIntoView({ behavior: 'smooth' });
            }}
            aria-label="Solver Section"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </a>
        </div>

        {/* Hero Section - Introduction */}
        <section id="introduction" className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white pb-6 sm:pb-8 min-h-[400px] sm:min-h-[500px] flex items-center overflow-visible scroll-margin-top-24">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
            <div className="absolute inset-0" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1500&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'overlay'
            }} />
          </div>
          <div className="container px-4 sm:px-6 md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 py-10 sm:py-12 md:py-16 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl hero-content"
            >
              <div className="mb-3 sm:mb-4 md:mb-6">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="inline-block bg-gradient-to-r from-blue-600 to-blue-400 text-white px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2 rounded-full shadow-lg mb-2 sm:mb-3 md:mb-4 coming-soon-banner"
                >
                  <span className="font-bold text-xs sm:text-sm md:text-lg tracking-wide coming-soon-text">NEW FEATURE</span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-lg sm:text-xl md:text-2xl font-bold text-white/90"
                >
                  Sea Wolf Guide & Solver
                </motion.div>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight text-white">
                Master McKinsey's Sea Wolf Game
              </h1>
              <p className="text-sm sm:text-base md:text-xl lg:text-2xl text-white/90 mb-5 sm:mb-6 md:mb-10 leading-relaxed">
                The newest addition to McKinsey Solve, where you'll restore ocean ecosystems by strategically selecting microbes. Your success depends on matching microbe characteristics to each site's specific needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 mt-5 sm:mt-6 md:mt-8">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const el = document.getElementById('solver');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="group relative px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-sm sm:text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                >
                  Launch Solver
                  <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowVideoModal(true)}
                  className="group relative px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-gray-700 to-gray-600 text-white text-sm sm:text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                >
                  Watch Tutorial
                  <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </motion.button>
                <Link to="/seawolf-practice">
                  <motion.span
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-block px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 bg-white/20 border-2 border-white/80 text-white text-sm sm:text-base md:text-lg rounded-xl shadow-md hover:bg-white/30 hover:border-white transition-all duration-300"
                  >
                    Run the Simulation
                  </motion.span>
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute top-[80px] right-10 md:right-20 w-[300px] sm:w-[350px] md:w-[400px] aspect-[2/3] z-10 hidden md:block hero-image"
          >
            <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
              <img 
                src={seaWolfImage} 
                alt="Sea Wolf Game" 
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              />
            </div>
          </motion.div>
        </section>

        {/* Video Modal */}
        <AnimatePresence>
          {showVideoModal && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
              onClick={() => setShowVideoModal(false)}
            >
              <motion.div 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                className="bg-white rounded-2xl p-3 sm:p-4 max-w-4xl w-full shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                {/* Video Tabs */}
                <div className="flex space-x-2 mb-3 sm:mb-4 border-b border-gray-200 overflow-x-auto pb-1 sm:pb-0">
                  {videoTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveVideoTab(tab.id)}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                        activeVideoTab === tab.id
                          ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      {tab.title}
                    </button>
                  ))}
                </div>

                {/* Video Container */}
                <div className="relative pb-[56.25%] h-0 rounded-lg overflow-hidden bg-gray-100">
                  {videoTabs.map((tab) => (
                    <div
                      key={tab.id}
                      className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 ${
                        activeVideoTab === tab.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                    >
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube-nocookie.com/embed/${tab.videoId}?rel=0&modestbranding=1`}
                        title={tab.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ))}
                </div>

                <div className="mt-3 sm:mt-4 flex justify-end">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowVideoModal(false)}
                    className="px-4 sm:px-6 py-1.5 sm:py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                  >
                    Close
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Key Metrics Section */}
        <section className="py-6 sm:py-8 bg-gradient-to-b from-blue-50/50 to-white relative scroll-margin-top-24">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-blue-100"
              >
                <div className="text-xl sm:text-2xl bg-blue-50 p-2 rounded-lg">⏱️</div>
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-800">Time Limit</h3>
                  <p className="text-xs sm:text-sm text-gray-600">30 minutes total thinking time</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-blue-100"
              >
                <div className="text-xl sm:text-2xl bg-blue-50 p-2 rounded-lg">🎯</div>
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-800">Game Structure</h3>
                  <p className="text-xs sm:text-sm text-gray-600">3 ocean sites, 5 steps each</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-blue-100"
              >
                <div className="text-xl sm:text-2xl bg-blue-50 p-2 rounded-lg">💡</div>
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-800">Success Metric</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Treatment effectiveness score</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="lg:col-span-1 lg:ml-[-1rem] lg:mr-[-8rem] p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <div className="text-xl sm:text-2xl bg-blue-50 p-1.5 sm:p-2 rounded-lg">🎧</div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-800">Listen & Learn</h3>
                </div>
                <p className="text-xs text-gray-600 mb-2 sm:mb-3">
                  Prefer to learn by listening? Tune into our expert walkthrough.
                </p>
                <div className="bg-white rounded-lg p-2 sm:p-3 shadow-inner">
                  <audio 
                    controls 
                    className="w-full h-8 sm:h-10"
                    controlsList="nodownload"
                    style={{ minWidth: '100%' }}
                  >
                    <source src={seaWolfPodcast} type="audio/wav" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Game Introduction Section - Welcome to Sea Wolf */}
        <section id="introduction" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-4 sm:px-6 relative scroll-margin-top-24">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold section-title mb-4 sm:mb-6">Welcome to the Sea Wolf Challenge</h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">
                A sophisticated simulation where you'll analyze and select microbes to restore contaminated ocean sites. Your strategic decisions directly impact your success score.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="feature-card rounded-2xl p-6 sm:p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-bl-full transform translate-x-12 sm:translate-x-16 -translate-y-12 sm:-translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-tr-full transform -translate-x-12 sm:-translate-x-16 translate-y-12 sm:translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6 bg-gradient-to-br from-blue-100 to-blue-50">🌊</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">The Latest Addition to McKinsey Solve</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    Since January 2025, the Sea Wolf Game has become a mandatory component of the McKinsey Problem Solving Game (PSG). Following its successful trial in 2024, this innovative assessment evaluates your ability to analyze complex data and make strategic decisions under time pressure.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="feature-card rounded-2xl p-6 sm:p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-bl-full transform translate-x-12 sm:translate-x-16 -translate-y-12 sm:-translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-tr-full transform -translate-x-12 sm:-translate-x-16 translate-y-12 sm:translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6 bg-gradient-to-br from-blue-100 to-blue-50">🎯</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">Success Criteria</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">
                    Your score depends on three critical factors:
                  </p>
                  <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Average values of three attributes must fall within site's desired range</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>At least one microbe must have the desired trait</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>No microbe should have the undesired trait</span>
                    </li>
                  </ul>
                  <div className="tip-box p-3 sm:p-4 mt-3 sm:mt-4 bg-gradient-to-r from-blue-50 to-blue-100/50 border-l-4 border-blue-400">
                    <p className="text-xs sm:text-sm text-blue-700 flex items-start gap-2">
                      <span className="text-lg sm:text-xl mt-0.5">💡</span>
                      <span>Each criterion missed results in a 20% penalty. Aim for 100% by meeting all requirements!</span>
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="feature-card rounded-2xl p-6 sm:p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-bl-full transform translate-x-12 sm:translate-x-16 -translate-y-12 sm:-translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-tr-full transform -translate-x-12 sm:-translate-x-16 translate-y-12 sm:translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6 bg-gradient-to-br from-blue-100 to-blue-50">⏱️</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">Time Management</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">
                    With 30 minutes total thinking time, efficient decision-making is crucial. Here's how to allocate your time:
                  </p>
                  <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>~10 minutes per ocean site</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>~30 seconds per microbe decision</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Most time should be spent on Step 4 (final microbe selection)</span>
                    </li>
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="feature-card rounded-2xl p-6 sm:p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-bl-full transform translate-x-12 sm:translate-x-16 -translate-y-12 sm:-translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-100 rounded-tr-full transform -translate-x-12 sm:-translate-x-16 translate-y-12 sm:translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6 bg-gradient-to-br from-blue-100 to-blue-50">📋</div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">Game Structure</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">
                    Each ocean site follows a five-step process:
                  </p>
                  <ol className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-600 list-decimal list-inside">
                    <li className="ml-2">Set microbe characteristics (filter selection)</li>
                    <li className="ml-2">Categorize 10 microbes (current/next/discard)</li>
                    <li className="ml-2">Build prospect pool (select 4 from 12)</li>
                    <li className="ml-2">Submit final 3 microbes</li>
                    <li className="ml-2">Review saved microbes for next site</li>
                  </ol>
                  <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50/50 to-transparent rounded-lg border border-blue-100">
                    <p className="text-xs sm:text-sm text-blue-700">
                      A comprehensive tutorial before the timed session will guide you through the specific objectives and scoring criteria.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Game Flow Section - 5 Steps */}
        <section id="game-flow" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white px-4 sm:px-6 relative scroll-margin-top-24">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold section-title">Game Flow: 5 Steps × 3 Ocean Sites</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {gameSteps.map((step, i) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="feature-card rounded-2xl p-6 sm:p-8"
                >
                  <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6">{step.icon}</div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800">Step {step.step}: {step.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{step.description}</p>
                  <div className="tip-box p-3 sm:p-4 mt-2 sm:mt-4">
                    <p className="text-xs sm:text-sm text-blue-700">💡 {step.tip}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Scenarios Section - Scoring Grid */}
        <section id="scenarios" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-4 sm:px-6 relative scroll-margin-top-24">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold section-title">Scoring & Scenarios</h2>
            </div>
            
            {/* Scoring Table */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 mb-8 sm:mb-12">
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-gray-800">Scoring Criteria</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-600">Criteria</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-600">Goal</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-600">Penalty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600">Attribute A average in range?</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-green-600">✅ Yes</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-red-600">−20% if not</td>
                    </tr>
                    <tr>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600">Attribute B average in range?</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-green-600">✅ Yes</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-red-600">−20% if not</td>
                    </tr>
                    <tr>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600">Attribute C average in range?</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-green-600">✅ Yes</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-red-600">−20% if not</td>
                    </tr>
                    <tr>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600">At least 1 desired trait?</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-green-600">✅ Yes</td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-red-600">−20% if none</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-600">No undesired trait?</td>
                      <td className="px-6 py-4 text-sm text-green-600">✅ Yes</td>
                      <td className="px-6 py-4 text-sm text-red-600">−20% if any present</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Scenarios Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {scenarios.map((scenario, i) => (
                <motion.div
                  key={scenario.type}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="scenario-card rounded-xl p-6 text-center"
                >
                  <div className="text-4xl mb-4">{scenario.icon}</div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-800">{scenario.type}</h3>
                  <div className="text-2xl font-bold text-blue-600 mb-2">{scenario.score}</div>
                  <p className="text-gray-600 text-sm">{scenario.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Strategy Section - Strategy Tips */}
        <section id="strategy" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-4 sm:px-6 relative scroll-margin-top-24">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold section-title">Strategy Tips</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="feature-card rounded-2xl p-6 sm:p-8"
              >
                <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6">⏱️</div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800">Time Management</h3>
                <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>30 minutes total (~10 minutes per site)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>Most time goes into Step 4</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>Don't spend too long finding a perfect combo</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="feature-card rounded-2xl p-6 sm:p-8"
              >
                <div className="feature-icon text-2xl sm:text-3xl mb-4 sm:mb-6">🧮</div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800">Quick Math Tip</h3>
                <div className="tip-box p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-blue-700">
                    Instead of averaging, sum the 3 microbes' values. If the site wants attributes between 8–10, your total should be between <strong>24–30</strong>.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="feature-card rounded-2xl p-10 sm:p-16 relative overflow-hidden md:col-span-2 mt-8 md:mt-12"
              >
                <div className="absolute top-0 right-0 w-24 sm:w-28 h-24 sm:h-28 bg-blue-100 rounded-bl-full transform translate-x-12 sm:translate-x-14 -translate-y-12 sm:-translate-y-14"></div>
                <div className="absolute bottom-0 left-0 w-24 sm:w-28 h-24 sm:h-28 bg-blue-100 rounded-tr-full transform -translate-x-12 sm:-translate-x-14 translate-y-12 sm:translate-y-14"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                    <span className="text-3xl sm:text-4xl bg-blue-100 w-14 h-14 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center shadow-inner">📊</span>
                    <div className="flex-1">
                      <h3 className="text-blue-600 font-bold text-xl sm:text-2xl">Learn with our data sets</h3>
                      <p className="text-blue-700 text-lg sm:text-xl mt-2">Practice with our curated Sea Wolf scenarios to master the game</p>
                      <div className="h-1 w-10 sm:w-12 bg-gradient-to-r from-blue-200 to-blue-100 rounded mt-1 sm:mt-2"></div>
                    </div>
                    <div className="relative">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="h-0.5 w-6 sm:w-8 bg-gradient-to-r from-transparent to-blue-500"></div>
                          <span className="text-blue-500 font-medium tracking-wider animate-pulse text-xs sm:text-sm">PRESS ME</span>
                          <div className="h-0.5 w-6 sm:w-8 bg-gradient-to-l from-transparent to-blue-500"></div>
                        </div>
                      </div>
                      <motion.button 
                        whileHover={{ scale: 1.1, rotate: 180 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setShowEcosystemData(!showEcosystemData)}
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white shadow-lg transform transition-transform duration-200 group"
                      >
                        <span className="text-lg sm:text-xl transform group-hover:rotate-12 transition-transform duration-200">🔌</span>
                      </motion.button>
                    </div>
                  </div>
                  <AnimatePresence>
                    {showEcosystemData && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-blue-50 rounded-lg p-3 sm:p-4 hover:bg-blue-100 transition-colors cursor-pointer group mb-3 sm:mb-4"
                          onClick={() => setShowMissionImpossible((v) => !v)}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl group-hover:scale-110 transition-transform">🧬</span>
                            <div className="flex-1">
                              <h4 className="font-medium text-blue-900 text-base sm:text-lg">Mission Impossible</h4>
                              <p className="text-xs sm:text-sm text-blue-700">A legendary Sea Wolf microbe set</p>
                            </div>
                            <motion.div
                              animate={{ rotate: showMissionImpossible ? 90 : 0 }}
                              className="text-blue-600 text-lg sm:text-xl ml-2"
                            >
                              ▶
                            </motion.div>
                          </div>
                        </motion.div>
                        <AnimatePresence>
                          {showMissionImpossible && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.4 }}
                              className="overflow-hidden"
                            >
                              <SiteInfoCard />
                              <div className="flex justify-end mb-2">
                                <button
                                  onClick={() => setShowFloatingMicrobes(true)}
                                  className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                                  title="Open in Sidebar"
                                >
                                  <span role="img" aria-label="sidebar">📋</span> Open in Sidebar to Test with our Solver
                                </button>
                              </div>
                              <MicrobeCardsGrid />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* For the Gram accordion */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-blue-50 rounded-lg p-3 sm:p-4 hover:bg-blue-100 transition-colors cursor-pointer group mb-3 sm:mb-4"
                          onClick={() => setShowForTheGram((v) => !v)}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl group-hover:scale-110 transition-transform">🦠</span>
                            <div className="flex-1">
                              <h4 className="font-medium text-blue-900 text-base sm:text-lg">For the Gram</h4>
                              <p className="text-xs sm:text-sm text-blue-700">A microbial challenge for the ages</p>
                            </div>
                            <motion.div
                              animate={{ rotate: showForTheGram ? 90 : 0 }}
                              className="text-blue-600 text-lg sm:text-xl ml-2"
                            >
                              ▶
                            </motion.div>
                          </div>
                        </motion.div>
                        <AnimatePresence>
                          {showForTheGram && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.4 }}
                              className="overflow-hidden"
                            >
                              <ForTheGramSiteInfoCard />
                              <div className="flex justify-end mb-2">
                                <button
                                  onClick={() => setShowFloatingForTheGram(true)}
                                  className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                                  title="Open in Sidebar"
                                >
                                  <span role="img" aria-label="sidebar">📋</span> Open in Sidebar to Test with our Solver
                                </button>
                              </div>
                              <ForTheGramMicrobeCardsGrid />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* Biotic Madness accordion */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-blue-50 rounded-lg p-3 sm:p-4 hover:bg-blue-100 transition-colors cursor-pointer group mb-3 sm:mb-4"
                          onClick={() => setShowBioticMadness((v) => !v)}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl group-hover:scale-110 transition-transform">🧫</span>
                            <div className="flex-1">
                              <h4 className="font-medium text-blue-900 text-base sm:text-lg">Biotic Madness</h4>
                              <p className="text-xs sm:text-sm text-blue-700">A challenging microbial environment</p>
                            </div>
                            <motion.div
                              animate={{ rotate: showBioticMadness ? 90 : 0 }}
                              className="text-blue-600 text-lg sm:text-xl ml-2"
                            >
                              ▶
                            </motion.div>
                          </div>
                        </motion.div>
                        <AnimatePresence>
                          {showBioticMadness && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.4 }}
                              className="overflow-hidden"
                            >
                              <BioticMadnessSiteInfoCard />
                              <div className="flex justify-end mb-2">
                                <button
                                  onClick={() => setShowFloatingBioticMadness(true)}
                                  className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                                  title="Open in Sidebar"
                                >
                                  <span role="img" aria-label="sidebar">📋</span> Open in Sidebar to Test with our Solver
                                </button>
                              </div>
                              <BioticMadnessMicrobeCardsGrid />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* Feel the Burn accordion */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-blue-50 rounded-lg p-3 sm:p-4 hover:bg-blue-100 transition-colors cursor-pointer group mb-3 sm:mb-4"
                          onClick={() => setIsFeelTheBurnExpanded((v) => !v)}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl group-hover:scale-110 transition-transform">🔥</span>
                            <div className="flex-1">
                              <h4 className="font-medium text-blue-900 text-base sm:text-lg">Feel the Burn</h4>
                              <p className="text-xs sm:text-sm text-blue-700">A high-pressure, high-heat scenario</p>
                            </div>
                            <motion.div
                              animate={{ rotate: isFeelTheBurnExpanded ? 90 : 0 }}
                              className="text-blue-600 text-lg sm:text-xl ml-2"
                            >
                              ▶
                            </motion.div>
                          </div>
                        </motion.div>
                        <AnimatePresence>
                          {isFeelTheBurnExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.4 }}
                              className="overflow-hidden"
                            >
                              <FeelTheBurnSiteInfoCard />
                              <div className="flex justify-end mb-2">
                                <button
                                  onClick={() => setShowFeelTheBurnSidebars(true)}
                                  className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                                  title="Open in Sidebar"
                                >
                                  <span role="img" aria-label="sidebar">📋</span> Open in Sidebar to Test with our Solver
                                </button>
                              </div>
                              <FeelTheBurnMicrobeCardsGrid />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* Flex Force accordion */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-blue-50 rounded-lg p-3 sm:p-4 hover:bg-blue-100 transition-colors cursor-pointer group mb-3 sm:mb-4"
                          onClick={() => setShowFlexForce((v) => !v)}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl group-hover:scale-110 transition-transform">🤸</span>
                            <div className="flex-1">
                              <h4 className="font-medium text-blue-900 text-base sm:text-lg">Flex Force</h4>
                              <p className="text-xs sm:text-sm text-blue-700">A test of movement and flexibility</p>
                            </div>
                            <motion.div
                              animate={{ rotate: showFlexForce ? 90 : 0 }}
                              className="text-blue-600 text-lg sm:text-xl ml-2"
                            >
                              ▶
                            </motion.div>
                          </div>
                        </motion.div>
                        <AnimatePresence>
                          {showFlexForce && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.4 }}
                              className="overflow-hidden"
                            >
                              <FlexForceSiteInfoCard />
                              <div className="flex justify-end mb-2">
                                <button
                                  onClick={() => setShowFloatingFlexForce(true)}
                                  className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                                  title="Open in Sidebar"
                                >
                                  <span role="img" aria-label="sidebar">📋</span> Open in Sidebar to Test with our Solver
                                </button>
                              </div>
                              <FlexForceMicrobeCardsGrid />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA to Simulation */}
        <section className="py-8 px-4 sm:px-6 bg-gradient-to-r from-blue-50 to-sky-50/80 border-y border-slate-200/60">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-700 text-sm sm:text-base mb-4">
              Want to practise with coach feedback? Run the Sea Wolf Simulation to make decisions yourself and get post-run feedback.
            </p>
            <Link
              to="/seawolf-practice"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
            >
              Run the Sea Wolf Simulation
            </Link>
          </div>
        </section>

        {/* Solver Section - Actual Solver UI */}
        <section id="solver" className="py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-6 relative scroll-margin-top-24">
          <div className="max-w-7xl mx-auto">
            <SeawolfSolverMethodSection />
          </div>
        </section>

        {/* Floating Microbe Sidebar */}
        {showFloatingMicrobes && (
          <>
            {/* Mobile bottom split view */}
            <div className="md:hidden fixed bottom-0 left-0 w-full flex flex-row z-[10000]">
              {/* Left bottom section: Site Info */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingMicrobes(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Practice with this dataset using our solver below!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <SiteInfoCard />
                </div>
              </div>
              {/* Right bottom section: Microbes */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingMicrobes(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Review these microbes carefully!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <MicrobeCardsGrid />
                </div>
              </div>
            </div>

            {/* Desktop sidebar layout */}
            <div className="hidden md:block">
              {/* Left sidebar: Site Info */}
              <div
                style={{
                  position: 'fixed',
                  left: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 320,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingMicrobes(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    Practice with this dataset using our solver below! Input these values manually or use them to test your own calculations.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <SiteInfoCard />
                </div>
              </div>
              {/* Right sidebar: Microbes */}
              <div
                style={{
                  position: 'fixed',
                  right: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 340,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingMicrobes(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    Review these microbes carefully! Each one has unique attributes that could be perfect for your solution.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <MicrobeCardsGrid />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Floating For the Gram Sidebar */}
        {showFloatingForTheGram && (
          <>
            {/* Mobile bottom split view */}
            <div className="md:hidden fixed bottom-0 left-0 w-full flex flex-row z-[10000]">
              {/* Left bottom section: Site Info */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingForTheGram(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Challenge yourself with this unique dataset!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <ForTheGramSiteInfoCard />
                </div>
              </div>
              {/* Right bottom section: Microbes */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingForTheGram(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Study these microbes carefully!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <ForTheGramMicrobeCardsGrid />
                </div>
              </div>
            </div>

            {/* Desktop sidebar layout */}
            <div className="hidden md:block">
              {/* Left sidebar: For the Gram Site Info */}
              <div
                style={{
                  position: 'fixed',
                  left: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 320,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingForTheGram(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    Challenge yourself with this unique dataset! Use our solver below to find the optimal solution.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <ForTheGramSiteInfoCard />
                </div>
              </div>
              {/* Right sidebar: For the Gram Microbes */}
              <div
                style={{
                  position: 'fixed',
                  right: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 340,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingForTheGram(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    These microbes present an interesting challenge! Study their attributes carefully.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <ForTheGramMicrobeCardsGrid />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Floating Biotic Madness Sidebar */}
        {showFloatingBioticMadness && (
          <>
            {/* Mobile bottom split view */}
            <div className="md:hidden fixed bottom-0 left-0 w-full flex flex-row z-[10000]">
              {/* Left bottom section: Site Info */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingBioticMadness(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Test your skills with this complex scenario!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <BioticMadnessSiteInfoCard />
                </div>
              </div>
              {/* Right bottom section: Microbes */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingBioticMadness(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Analyze these microbes carefully!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <BioticMadnessMicrobeCardsGrid />
                </div>
              </div>
            </div>

            {/* Desktop sidebar layout */}
            <div className="hidden md:block">
              {/* Left sidebar: Biotic Madness Site Info */}
              <div
                style={{
                  position: 'fixed',
                  left: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 320,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingBioticMadness(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    Put your skills to the test with this complex scenario! Perfect for advanced practice.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <BioticMadnessSiteInfoCard />
                </div>
              </div>
              {/* Right sidebar: Biotic Madness Microbes */}
              <div
                style={{
                  position: 'fixed',
                  right: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 340,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingBioticMadness(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    These microbes present a complex challenge! Analyze their attributes carefully.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <BioticMadnessMicrobeCardsGrid />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Feel the Burn Sidebars */}
        {showFeelTheBurnSidebars && (
          <>
            {/* Mobile bottom split view */}
            <div className="md:hidden fixed bottom-0 left-0 w-full flex flex-row z-[10000]">
              {/* Left bottom section: Site Info */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFeelTheBurnSidebars(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Master high-pressure scenarios!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <FeelTheBurnSiteInfoCard />
                </div>
              </div>
              {/* Right bottom section: Microbes */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFeelTheBurnSidebars(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Test your ability to handle pressure!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <FeelTheBurnMicrobeCardsGrid />
                </div>
              </div>
            </div>

            {/* Desktop sidebars */}
            <div className="hidden md:block">
              {/* Left sidebar: Feel the Burn Site Info */}
              <div
                style={{
                  position: 'fixed',
                  left: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 320,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFeelTheBurnSidebars(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    Master high-pressure scenarios with this intense dataset! Great for building confidence.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FeelTheBurnSiteInfoCard />
                </div>
              </div>

              {/* Right sidebar: Feel the Burn Microbes */}
              <div
                style={{
                  position: 'fixed',
                  right: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 340,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFeelTheBurnSidebars(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    These microbes will test your ability to handle pressure! Study their attributes carefully.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FeelTheBurnMicrobeCardsGrid />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Floating Flex Force Sidebar */}
        {showFloatingFlexForce && (
          <>
            {/* Mobile bottom split view */}
            <div className="md:hidden fixed bottom-0 left-0 w-full flex flex-row z-[10000]">
              {/* Left bottom section: Site Info */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingFlexForce(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Test your flexibility with this dataset!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <FlexForceSiteInfoCard />
                </div>
              </div>
              {/* Right bottom section: Microbes */}
              <div
                style={{
                  width: '50%',
                  height: '35vh',
                  background: 'white',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  padding: 12,
                }}
              >
                <button
                  onClick={() => setShowFloatingFlexForce(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text text-xs">
                    Study these adaptable microbes!
                  </p>
                </div>
                <div style={{ marginTop: 8 }}>
                  <FlexForceMicrobeCardsGrid />
                </div>
              </div>
            </div>

            {/* Desktop sidebar layout */}
            <div className="hidden md:block">
              {/* Left sidebar: Flex Force Site Info */}
              <div
                style={{
                  position: 'fixed',
                  left: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 320,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingFlexForce(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    Test your flexibility with this dynamic dataset! Perfect for practicing different approaches.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FlexForceSiteInfoCard />
                </div>
              </div>

              {/* Right sidebar: Flex Force Microbes */}
              <div
                style={{
                  position: 'fixed',
                  right: 24,
                  top: 80,
                  width: '85%',
                  maxWidth: 340,
                  height: '80vh',
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  overflowY: 'auto',
                  zIndex: 10000,
                  padding: 16,
                }}
              >
                <button
                  onClick={() => setShowFloatingFlexForce(false)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#333',
                    zIndex: 2,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="dataset-header">
                  <p className="dataset-header-text">
                    These microbes will test your adaptability! Study their flexible attributes carefully.
                  </p>
                </div>
                <div style={{ marginTop: 12 }}>
                  <FlexForceMicrobeCardsGrid />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Modal for Scoring Details */}
        <AnimatePresence>
          {showScoringDetails && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
            >
              <motion.div 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                className="bg-white rounded-2xl p-4 sm:p-8 max-w-2xl w-full shadow-xl"
              >
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-800">Scoring Guide</h3>
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-sm sm:text-base text-gray-600">
                    Your score depends on how well your chosen microbes match each site's needs:
                  </p>
                  <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>100% = perfect match</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">×</span>
                      <span>−20% penalty per mistake (per attribute, missing trait, or undesired trait)</span>
                    </li>
                  </ul>
                  <div className="tip-box p-3 sm:p-4 mt-3 sm:mt-4">
                    <p className="text-xs sm:text-sm text-blue-700">
                      💡 If you can't find a perfect combo in 4 minutes, submit your best result (80% or 60%) and move on.
                    </p>
                  </div>
                </div>
                <div className="mt-6 sm:mt-8 flex justify-end">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowScoringDetails(false)}
                    className="px-4 sm:px-6 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Close
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

function SeawolfSolverMethodSection() {
  const navigate = useNavigate();
  const solverContentRef = useRef(null);
  const [activeStep, setActiveStep] = useState(1);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [mathSymbols, setMathSymbols] = useState([]);
  const [showSolver, setShowSolver] = useState(false);
  const [ecosystemData, setEcosystemData] = useState(null);
  const [prefillFromGame, setPrefillFromGame] = useState(false);
  const { canAccess } = useFeatureAccess();

  // Math symbol animation logic
  useEffect(() => {
    // Generate 18 random math symbols with random positions, scale, and opacity
    const symbols = ['∑', '∏', '∆', '∇', '∫', '≈', '≠', '∞', '±', '⊕', '⊗', '∈', '∉', '∩', '∪'];
    const positions = Array.from({ length: 18 }, (_, index) => {
      return {
        symbol: symbols[Math.floor(Math.random() * symbols.length)],
        top: Math.floor(Math.random() * 85) + 5, // 5% to 90%
        left: Math.floor(Math.random() * 85) + 5,
        scale: Math.random() * 0.5 + 1,
        opacity: Math.random() * 0.3 + 0.1,
        delay: index * 0.13
      };
    });
    setMathSymbols(positions);
  }, []);

  // Prefill from game: hash #solver&prefill=<base64> opens solver with prefilled data
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return;
    const hash = window.location.hash.slice(1);
    if (!solverHashTargetsSection(hash)) return;
    const prefillMatch = hash.match(/prefill=([^&]*)/);
    if (!prefillMatch || !prefillMatch[1]) return;
    try {
      const raw = decodeURIComponent(atob(decodeURIComponent(prefillMatch[1])));
      const data = JSON.parse(raw);
      if (data && Array.isArray(data.microbes) && data.microbes.length > 0 && Array.isArray(data.attributes)) {
        setEcosystemData(data);
        setShowSolver(true);
        setActiveStep(2);
        setPrefillFromGame(true);
      }
    } catch (e) {
      console.warn('Sea Wolf solver prefill decode failed', e);
    }
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    if (!showSolver || activeStep !== 2) return;

    const scrollTarget = () => {
      const solverSection = document.getElementById('solver');
      const target = solverContentRef.current || solverSection;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const rafId = window.requestAnimationFrame(() => {
      window.setTimeout(scrollTarget, 120);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [showSolver, activeStep, prefillFromGame]);

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-4 sm:px-6 relative mt-8 sm:mt-12">
      {/* Desktop container */}
      <div
        ref={solverContentRef}
        className="bg-white/95 border border-blue-100 rounded-2xl shadow-inner p-4 sm:p-6 md:p-8 relative overflow-hidden md:block hidden"
        style={{zIndex:1}}
      >
        {/* Math Symbols Animation Container */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{zIndex:0}}>
          {mathSymbols.map((item, index) => (
            <motion.div
              key={`${index}-${item.symbol}`}
              className="math-symbol"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [item.opacity, item.opacity + 0.2],
                y: [0, -15, 0],
                x: [-10, 10, -10],
                rotate: [-8, 8, -8],
                scale: [item.scale, item.scale * 1.1, item.scale]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                repeatType: "reverse",
                delay: item.delay,
                ease: "easeInOut"
              }}
              style={{
                top: `${item.top}%`,
                left: `${item.left}%`,
              }}
            >
              {item.symbol}
            </motion.div>
          ))}
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-12">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 180 }}
              whileTap={{ scale: 0.9 }}
              className="rotate-button"
            >
              🎮
            </motion.div>
            <div className="title-container">
              <h2 className="premium-title text-xl sm:text-2xl md:text-3xl">Sea Wolf Solver</h2>
              <div className="title-underline"></div>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Let Cognidoor crunch the numbers while you relax</p>
            </div>
          </div>
          {/* Progress Steps */}
              <div className="progress-container">
                <div className="steps-wrapper">
                  {[1, 2, 3].map((step) => {
                    const tooltipText =
                      step === 1
                        ? 'Click below to enter your microbe data and ace Sea Wolf'
                        : step === 2
                        ? 'Input Data'
                    : 'View Results';
                    return (
                      <motion.div
                        key={step}
                        className={`step-container ${step <= activeStep ? 'step-active' : ''}`}
                        onClick={() => {
                      if (step === 1) {
                        setActiveStep(1);
                        setShowSolver(false);
                      } else if (step === 2 && showSolver) {
                        setActiveStep(2);
                      } else if (step === 3 && ecosystemData?.solved) {
                        setActiveStep(3);
                      }
                        }}
                    whileHover={{ scale: (step === 1 || (step === 2 && showSolver) || (step === 3 && ecosystemData?.solved)) ? 1.05 : 1 }}
                    whileTap={{ scale: (step === 1 || (step === 2 && showSolver) || (step === 3 && ecosystemData?.solved)) ? 0.95 : 1 }}
                    style={{ cursor: (step === 1 || (step === 2 && showSolver) || (step === 3 && ecosystemData?.solved)) ? 'pointer' : 'default' }}
                        onMouseEnter={() => setHoveredStep(step)}
                        onMouseLeave={() => setHoveredStep(null)}
                      >
                        {/* Tooltip */}
                        {hoveredStep === step && (
                          <div style={{
                            position: 'absolute',
                            top: '-30px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#111',
                            color: '#fff',
                            padding: '3px 10px',
                            borderRadius: '6px',
                            fontSize: '0.78rem',
                            whiteSpace: 'nowrap',
                            zIndex: 10,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            pointerEvents: 'none',
                            fontWeight: 400,
                            letterSpacing: '0.01em',
                          }}>
                            {tooltipText}
                          </div>
                        )}
                        <motion.div
                          className="step-number text-sm sm:text-base w-6 h-6 sm:w-8 sm:h-8"
                          animate={{
                            scale: step === activeStep ? 1.1 : 1,
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          {step}
                        </motion.div>
                        <span className="step-label text-xs sm:text-sm">
                      {step === 1 ? 'Choose Method' : step === 2 ? 'Input Data' : 'View Results'}
                        </span>
                      </motion.div>
                    );
                  })}
                  <div className="progress-track">
                    <motion.div
                      className="progress-fill"
                      initial={{ width: '0%' }}
                      animate={{ width: `${((activeStep - 1) / 2) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </div>

              {/* Method Selection Card */}
              {activeStep === 1 && !showSolver && (
                <div className="flex flex-col items-center justify-center mb-6 sm:mb-8 mt-8 sm:mt-12">
                  <motion.div
                    whileHover={{ scale: canAccess('seaWolfSolverManualEntry') ? 1.03 : 1 }}
                    whileTap={{ scale: canAccess('seaWolfSolverManualEntry') ? 0.98 : 1 }}
                    onClick={() => {
                      if (canAccess('seaWolfSolverManualEntry')) {
                        setShowSolver(true);
                        setActiveStep(2);
                      }
                    }}
                    className={`premium-card p-6 sm:p-8 md:p-12 rounded-2xl cursor-pointer relative w-full max-w-2xl ${!canAccess('seaWolfSolverManualEntry') ? 'opacity-60 pointer-events-none' : ''}`}
                    style={{ minWidth: 320 }}
                  >
                    <div className="animated-label">
                      <span className="animated-text text-xs sm:text-sm">Choose Me</span>
                    </div>
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <div className="flex items-center gap-3 sm:gap-6">
                        <div className="premium-icon text-2xl sm:text-3xl w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
                          ✍️
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">Manual Entry</h3>
                          <div className="h-1 w-12 sm:w-16 bg-gradient-to-r from-blue-400 to-transparent rounded"></div>
                        </div>
                      </div>
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 180 }}
                        whileTap={{ scale: 0.9 }}
                        className="rotate-button"
                      >
                        <span className="text-2xl sm:text-3xl">🎯</span>
                      </motion.div>
                    </div>
                    <p className="text-sm sm:text-lg text-gray-600 mb-4 sm:mb-6">
                      Input microbe data manually with our intuitive interface
                    </p>
                    <div className="unlock-text text-sm sm:text-lg">Assessment Feature</div>
                  </motion.div>
                  {!canAccess('seaWolfSolverManualEntry') && (
                    <div className="flex justify-center mt-2 relative w-full max-w-2xl">
                      <button
                        className="px-6 py-2 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md w-full max-w-xs"
                        style={{position:'relative'}}
                        onClick={() => navigate('/', { state: pricingState })}
                        onMouseEnter={e => {
                          const tooltip = e.currentTarget.nextSibling;
                          if (tooltip) tooltip.style.opacity = 1;
                        }}
                        onMouseLeave={e => {
                          const tooltip = e.currentTarget.nextSibling;
                          if (tooltip) tooltip.style.opacity = 0;
                        }}
                      >
                        View plans
                      </button>
                      <div style={{
                        position: 'absolute',
                        top: '-38px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#111',
                        color: '#fff',
                        padding: '6px 16px',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s',
                        fontWeight: 400,
                        letterSpacing: '0.01em',
                      }}>
                        Unlock manual entry with an eligible assessment or full-track plan
                      </div>
                    </div>
                  )}
                </div>
              )}

          {/* Solver Component */}
          {activeStep === 2 && showSolver && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white/95 border border-blue-100 rounded-2xl shadow-inner p-4 sm:p-6 md:p-8"
            >
              {prefillFromGame && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm flex items-center justify-between gap-2">
                  <span>Prefilled from your game — press <strong>Solve Ecosystem</strong> to get your best trio.</span>
                  <button type="button" onClick={() => setPrefillFromGame(false)} className="text-blue-600 hover:underline shrink-0" aria-label="Dismiss">Dismiss</button>
                </div>
              )}
              {canAccess('seaWolfSolverManualEntry') ? (
                <SeaWolfSolver 
                  initialData={ecosystemData}
                  onDataChange={(data) => {
                    setEcosystemData(data);
                  }}
                  onStateChange={(state) => {
                    // Optionally, you could also update ecosystemData here for live updates
                  }}
                  setActiveStep={setActiveStep}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="text-lg font-semibold text-blue-700 mb-2 text-center">Unlock the Sea Wolf Solver</div>
                  <div className="text-gray-600 mb-4 text-center max-w-md">Choose a plan that includes Sea Wolf access to unlock the solver and instant results.</div>
                  <div className="flex gap-4">
                    <button
                      className="px-6 py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md"
                      onClick={() => navigate('/', { state: pricingState })}
                    >
                      View plans
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Results Component */}
          {activeStep === 3 && ecosystemData?.solved && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white/95 border border-blue-100 rounded-2xl shadow-inner p-4 sm:p-6 md:p-8"
            >
              <SeaWolfResults 
                result={ecosystemData}
                loading={false}
                onSolveNext={() => {
                  // Reset the solver state
                  setEcosystemData(null);
                  // Go back to step 2 (input data)
                  setActiveStep(2);
                }}
              />
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Mobile container (simplified) */}
      <div className="md:hidden block relative">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="rotate-button">
              🎮
            </div>
            <div className="title-container">
              <h2 className="premium-title text-xl">Sea Wolf Solver</h2>
              <div className="title-underline"></div>
              <p className="text-sm text-gray-600 mt-1">Let Cognidoor crunch the numbers while you relax</p>
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="progress-container">
            <div className="steps-wrapper">
              {[1, 2, 3].map((step) => {
                return (
                  <motion.div
                    key={step}
                    className={`step-container ${step <= activeStep ? 'step-active' : ''}`}
                    onClick={() => {
                      if (step === 1) {
                        setActiveStep(1);
                        setShowSolver(false);
                      } else if (step === 2 && showSolver) {
                        setActiveStep(2);
                      } else if (step === 3 && ecosystemData?.solved) {
                        setActiveStep(3);
                      }
                    }}
                    whileHover={{ scale: (step === 1 || (step === 2 && showSolver) || (step === 3 && ecosystemData?.solved)) ? 1.05 : 1 }}
                    whileTap={{ scale: (step === 1 || (step === 2 && showSolver) || (step === 3 && ecosystemData?.solved)) ? 0.95 : 1 }}
                    style={{ cursor: (step === 1 || (step === 2 && showSolver) || (step === 3 && ecosystemData?.solved)) ? 'pointer' : 'default' }}
                  >
                    <motion.div
                      className="step-number text-sm w-6 h-6"
                      animate={{
                        scale: step === activeStep ? 1.1 : 1,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {step}
                    </motion.div>
                    <span className="step-label text-xs">
                      {step === 1 ? 'Choose Method' : step === 2 ? 'Input Data' : 'View Results'}
                    </span>
                  </motion.div>
                );
              })}
              <div className="progress-track">
                <motion.div
                  className="progress-fill"
                  initial={{ width: '0%' }}
                  animate={{ width: `${((activeStep - 1) / 2) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>

          {/* Method Selection Card */}
          {activeStep === 1 && !showSolver && (
            <div className="flex flex-col items-center justify-center mb-6 sm:mb-8 mt-8 sm:mt-12">
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setShowSolver(true);
                  setActiveStep(2);
                }}
                className="premium-card p-5 rounded-2xl cursor-pointer relative w-full"
              >
                <div className="animated-label">
                  <span className="animated-text text-xs">Choose Me</span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="premium-icon text-2xl w-10 h-10 flex items-center justify-center">
                      ✍️
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Manual Entry</h3>
                      <div className="h-1 w-10 bg-gradient-to-r from-blue-400 to-transparent rounded"></div>
                    </div>
                  </div>
                  <div className="rotate-button">
                    <span className="text-2xl">🎯</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Input microbe data manually with our intuitive interface
                </p>
                <div className="unlock-text text-sm">Assessment Feature</div>
              </motion.div>
              {!canAccess('seaWolfSolverManualEntry') && (
                <div className="flex justify-center mt-2 relative w-full max-w-2xl">
                  <button
                    className="px-6 py-2 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md w-full max-w-xs"
                    style={{position:'relative'}}
                    onClick={() => navigate('/', { state: pricingState })}
                    onMouseEnter={e => {
                      const tooltip = e.currentTarget.nextSibling;
                      if (tooltip) tooltip.style.opacity = 1;
                    }}
                    onMouseLeave={e => {
                      const tooltip = e.currentTarget.nextSibling;
                      if (tooltip) tooltip.style.opacity = 0;
                    }}
                  >
                    View plans
                  </button>
                  <div style={{
                    position: 'absolute',
                    top: '-38px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#111',
                    color: '#fff',
                    padding: '6px 16px',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 0.2s',
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                  }}>
                    Unlock manual entry with an eligible assessment or full-track plan
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Solver Component - No container for mobile */}
          {activeStep === 2 && showSolver && (
            <div className="mt-4">
              {prefillFromGame && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm flex items-center justify-between gap-2">
                  <span>Prefilled from your game — press <strong>Solve Ecosystem</strong> to get your best trio.</span>
                  <button type="button" onClick={() => setPrefillFromGame(false)} className="text-blue-600 hover:underline shrink-0" aria-label="Dismiss">Dismiss</button>
                </div>
              )}
              {canAccess('seaWolfSolverManualEntry') ? (
                <SeaWolfSolver 
                  initialData={ecosystemData}
                  onDataChange={(data) => {
                    setEcosystemData(data);
                  }}
                  onStateChange={(state) => {
                    // Optionally, you could also update ecosystemData here for live updates
                  }}
                  setActiveStep={setActiveStep}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="text-lg font-semibold text-blue-700 mb-2 text-center">Unlock the Sea Wolf Solver</div>
                  <div className="text-gray-600 mb-4 text-center max-w-md">Choose a plan that includes Sea Wolf access to unlock the solver and instant results.</div>
                  <div className="flex gap-4">
                    <button
                      className="px-6 py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md"
                      onClick={() => navigate('/', { state: pricingState })}
                    >
                      View plans
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Results Component */}
          {activeStep === 3 && ecosystemData?.solved && (
            <div className="mt-4">
              <SeaWolfResults 
                result={ecosystemData}
                loading={false}
                onSolveNext={() => {
                  // Reset the solver state
                  setEcosystemData(null);
                  // Go back to step 2 (input data)
                  setActiveStep(2);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
} 
